import logoVariants from '@/assets/img/logo';
import iconsPng from '@/assets/img/png/icons';
import svgIcons from '@/assets/svg/icons';
import CustomButton from '@/components/atoms/CustomButton';
import MYOLinearGradient from '@/components/atoms/MYOLinearGradient';
import SvgIcon from '@/components/atoms/SvgIcon';
import MobileFirstPageTemplate from '@/components/templates/MobileFirstPageTemplate';
import {
  NO_OF_WELLNESS_DOMAINS,
  WELLNESS_DATA_MULTIPLIER,
  WELLNESS_DOMAINS_DISPLAY_LABELS,
  WELLNESS_DOMAINS_LIST,
} from '@/constants';
import { FORM_FIELD_WIDTH } from '@/constants/dimensions';
import { PUBLIC_SCREENER_QUESTION_AND_SCORE_DESCRIPTION_PER_DOMAIN } from '@/constants/publicScreenerQuestions';
import { MOBILE_DEFAULT_PADDING } from '@/constants/ui';
import useSubmitScreenerResult from '@/hooks/publicscreener/useSubmitScreenerResult';
import ControlledSliderExtended from '@/providers/DocumentUiProvider/components/SliderExtended/ControlledSliderExtended';
import colors from '@/theme/colors';
import { THealthDomain, THealthDomainScores } from '@/types/Health.types';
import { Box, Button, Image, Text, VStack } from '@chakra-ui/react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import useEmblaCarousel from 'embla-carousel-react';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import useTelemetry from '@/providers/TelemetryProvider/useTelemetry';
import { mixpanelEvents, PUBLIC_SCREENER_TYPE } from '@/constants/mixpanel';
import { FormProvider, useForm } from 'react-hook-form';
import { usePublicScreenerStore } from '@/store/usePublicScreenerStore';
import { TPublicScreenerFormForBE } from '@/types/PublicScreener.types';
import TurnstileWidget from '@/components/molecules/TurnstileWidget';
import TryScreenerOrganizationValidation from '@/modules/try-screener/TryScreenerOrganizationValidation';

const TryScreenerPage = () => {
  const { organizationSlug } = Route.useParams();
  const { trackEvent } = useTelemetry();

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const navigate = useNavigate();

  const { setOrganizationSlug, setResult: setResultToScreenerStore } = usePublicScreenerStore();

  const methods = useForm<Record<THealthDomain, { value: number }>>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const { formState, handleSubmit, watch } = methods;

  const { dirtyFields } = formState;

  const allFieldsDirty = Object.keys(dirtyFields).length === NO_OF_WELLNESS_DOMAINS;

  const [isOrgValid, setIsOrgValid] = useState<boolean>(false);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [totalSlides, setTotalSlides] = useState(0);

  const isLastSlide = currentSlide === totalSlides - 1;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    startIndex: 0,
    watchDrag: () => {}, // disables dragging
  });

  const goBackVisible = useMemo(() => currentSlide > 0, [currentSlide]);
  const currentFormData = watch(WELLNESS_DOMAINS_LIST[currentSlide]);

  const continueDisabled = currentFormData === undefined;

  const showNextSlide = useCallback(() => {
    trackEvent(mixpanelEvents.SCREENER_QUESTION_ANSWERED, {
      question_number: currentSlide + 1,
      answer: currentFormData.value,
      type: PUBLIC_SCREENER_TYPE,
    });

    setCurrentSlide((prev) => prev + 1);

    emblaApi?.scrollNext();

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [emblaApi, currentFormData, currentSlide]);

  const showPreviousSlide = () => {
    setCurrentSlide((prev) => prev - 1);

    emblaApi?.scrollPrev();

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onSuccessPostScreenerAnswers = (submissionId: string) => {
    navigate({
      to: '/try-screener/$organizationSlug/screener/success',
      params: { organizationSlug },
      search: { submissionId },
    });
  };

  const { mutate: postScreenerAnswers, isPending } = useSubmitScreenerResult(
    organizationSlug,
    turnstileToken,
    onSuccessPostScreenerAnswers,
  );

  const onSubmit = (data: Record<THealthDomain, { value: number }>) => {
    trackEvent(mixpanelEvents.SCREENER_QUESTION_ANSWERED, {
      question_number: currentSlide + 1,
      answer: currentFormData.value,
      type: PUBLIC_SCREENER_TYPE,
    });

    const dataEntries = Object.entries(data as Record<THealthDomain, Record<'value', number>>).map(
      ([domain, { value }]) => [domain, value] as [THealthDomain, number],
    );

    trackEvent(mixpanelEvents.SCREENER_COMPLETED, {
      total_questions: dataEntries.length,
      type: PUBLIC_SCREENER_TYPE,
    });

    const scoresPerDomain = Object.fromEntries(dataEntries) as THealthDomainScores;
    const totalScoreCalculated =
      Object.values(scoresPerDomain).reduce((acc, curr) => (acc += curr), 0) / NO_OF_WELLNESS_DOMAINS;

    const formData: TPublicScreenerFormForBE = {
      scores: scoresPerDomain,
      totalScore: totalScoreCalculated,
    };

    const wellnessDomainsMultiplied = { ...formData.scores };

    for (const key in wellnessDomainsMultiplied) {
      wellnessDomainsMultiplied[key as keyof THealthDomainScores] *= WELLNESS_DATA_MULTIPLIER;
    }

    setResultToScreenerStore({
      scores: wellnessDomainsMultiplied,
      totalScore: formData.totalScore * WELLNESS_DATA_MULTIPLIER,
    });

    setOrganizationSlug(organizationSlug);

    postScreenerAnswers({ formData });
  };

  const isLargeScreen = window.innerWidth > 450;
  const screenerWidth = isLargeScreen ? 600 : 330;

  const mainScreenerSlides = useMemo(
    () =>
      Object.entries(PUBLIC_SCREENER_QUESTION_AND_SCORE_DESCRIPTION_PER_DOMAIN).map(
        // questionDescription,
        ([domainName, { scoreDescription }]) => (
          <Fragment key={`domain-container-${domainName}`}>
            {/* Domain slide */}
            <VStack className="embla__slide" key={domainName}>
              <VStack paddingBottom={'20px'} gap={0}>
                <VStack
                  width={'70px'}
                  height={'70px'}
                  bg={'white'}
                  justifyContent={'center'}
                  alignItems={'center'}
                  borderRadius={'20px'}
                  boxShadow={'0px 8px 20px 0px #4E1AEF33'}
                  marginY={'10px'}
                >
                  <Image
                    src={iconsPng.wellnessDomainsPurple[domainName as THealthDomain]}
                    height={'40px'}
                    width={'40px'}
                  />
                </VStack>
                <Text variant="loraSectionTitle">{WELLNESS_DOMAINS_DISPLAY_LABELS[domainName as THealthDomain]}</Text>

                {/* <Text variant={'assessmentQuestionTitle'} maxWidth={'300px'}>
                  {questionDescription}
                </Text> */}
                <Text
                  variant={'assessmentQuestionText'}
                  lineHeight={'20px'}
                  marginTop="10px"
                  marginBottom="20px"
                  maxWidth={screenerWidth}
                >
                  {scoreDescription}
                </Text>

                <ControlledSliderExtended
                  minHeight="50px"
                  paddingX="16px"
                  paddingY="14px"
                  name={domainName}
                  healthDomain={domainName}
                  labelStart={'1 - Poor'}
                  labelEnd={'Excellent - 10'}
                />
              </VStack>

              {/* Continue and Go Back buttons of the Domain slide - visible on all domain slides except for the last one (Concerns) */}
              {isLastSlide ? (
                <VStack>
                  <Button
                    marginTop={'10px'}
                    variant={'formSubmit'}
                    width={FORM_FIELD_WIDTH}
                    isDisabled={!allFieldsDirty || !turnstileToken}
                    type="submit"
                    _hover={{
                      backgroundColor: continueDisabled ? 'secondary.400' : 'secondary.600',
                    }}
                    isLoading={isPending}
                  >
                    Finish
                  </Button>
                </VStack>
              ) : (
                <Button
                  marginTop={'10px'}
                  variant={'formSubmit'}
                  width={FORM_FIELD_WIDTH}
                  isDisabled={continueDisabled}
                  _hover={{
                    backgroundColor: continueDisabled ? 'secondary.400' : 'secondary.600',
                  }}
                  onClick={showNextSlide}
                >
                  Continue
                </Button>
              )}

              <CustomButton
                isTransparent
                labelHoverColor="primary.black"
                label="Go Back"
                onClick={showPreviousSlide}
                style={{
                  visibility: goBackVisible ? 'visible' : 'hidden',
                }}
              />
            </VStack>
          </Fragment>
        ),
      ),
    [continueDisabled, isLastSlide, goBackVisible, isPending],
  );

  // initial scroll slide fix
  useEffect(() => {
    if (emblaApi) {
      emblaApi?.scrollTo(0);

      if (totalSlides === 0) {
        setTotalSlides(emblaApi.slideNodes().length);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emblaApi]);

  if (!isOrgValid) {
    return (
      <MobileFirstPageTemplate>
        <TryScreenerOrganizationValidation
          isValid={isOrgValid}
          organizationSlug={organizationSlug}
          setIsValid={setIsOrgValid}
        />
      </MobileFirstPageTemplate>
    );
  }

  return (
    <MobileFirstPageTemplate headerHidden>
      <MYOLinearGradient height={'140px'}>
        <VStack width={'100%'} align={'center'} justify={'center'} gap={'8px'} position={'relative'}>
          {goBackVisible && (
            <Box position={'absolute'} left={isLargeScreen ? 10 : 7} top={isLargeScreen ? 10 : 9} onClick={showPreviousSlide} cursor={'pointer'}>
              <SvgIcon iconPath={svgIcons.chevronLeft} color={colors.extra.white} size={18} />
            </Box>
          )}
          <Image src={logoVariants.whiteNoText160x160} width={'40px'} height={'40px'} alt="my-omnia-logo" />
          <Text
            variant={'loraSmallTitle'}
            fontSize={'18px'}
            lineHeight={'22px'}
            color={'extra.white'}
            width={screenerWidth}
            paddingX={'20px'}
          >
            Tell us how you feel each of these parts of your life are currently.
          </Text>
        </VStack>
      </MYOLinearGradient>
      <VStack paddingInline={MOBILE_DEFAULT_PADDING} zIndex={1} maxWidth={'100%'}>
        <VStack width={'100%'}>
          <FormProvider {...methods}>
            <form style={{ width: '100vw' }} onSubmit={handleSubmit(onSubmit)}>
              <section className="embla">
                <div className="embla__viewport" ref={emblaRef}>
                  <div className="embla__container">{mainScreenerSlides}</div>
                </div>
              </section>
            </form>
          </FormProvider>
        </VStack>
      </VStack>
      <TurnstileWidget setTurnstileToken={setTurnstileToken} turnstileToken={turnstileToken} />
    </MobileFirstPageTemplate>
  );
};

export const Route = createFileRoute('/try-screener/$organizationSlug/screener/')({
  staticData: { title: 'Screener' },
  component: TryScreenerPage,
  validateSearch: (search: Record<string, unknown>): { source?: string } => ({
    source: search.source as string,
  }),
  beforeLoad: async ({ context: { auth } }) => {
    const isAuthenticated = auth?.isAuthenticated;
    if (isAuthenticated) {
      throw redirect({
        to: '/',
      });
    }
  },
});
